import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../../config/firebase";
import { doc, getDoc } from "firebase/firestore";
import { motion } from "framer-motion";

const ChampionshipBidDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bid, setBid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchBidDetails = async () => {
      try {
        setLoading(true);
        const bidDoc = await getDoc(doc(db, "championshipBids", id));
        
        if (bidDoc.exists()) {
          setBid({
            id: bidDoc.id,
            ...bidDoc.data(),
          });
        } else {
          setError("Bid not found");
        }
      } catch (error) {
        console.error("Error fetching bid details:", error);
        setError("Error loading bid details");
      } finally {
        setLoading(false);
      }
    };

    fetchBidDetails();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <div className="max-w-3xl mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600 dark:text-red-400 mb-4">{error}</h2>
            <button
              onClick={() => navigate("/admin/bids")}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Bids
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Championship Bid Details
          </h1>
          <button
            onClick={() => navigate("/admin/bids")}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Bids
          </button>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden"
        >
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6">
            <h2 className="text-2xl font-bold text-white">{bid?.clubName}</h2>
            <p className="text-blue-100 mt-1">
              Submitted: {bid?.submissionDate?.toDate().toLocaleString() || "N/A"}
            </p>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  Club Information
                </h3>
                <div className="space-y-2">
                  <div>
                    <span className="text-sm text-gray-500 dark:text-gray-400 block">
                      Club Name
                    </span>
                    <span className="text-gray-900 dark:text-white font-medium">
                      {bid?.clubName}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500 dark:text-gray-400 block">
                      Hosted Before
                    </span>
                    <span className="text-gray-900 dark:text-white font-medium">
                      {bid?.hostedBefore ? "Yes" : "No"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  Contact Information
                </h3>
                <div className="space-y-2">
                  <div>
                    <span className="text-sm text-gray-500 dark:text-gray-400 block">
                      Contact Name
                    </span>
                    <span className="text-gray-900 dark:text-white font-medium">
                      {bid?.contactName || "Not provided"}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500 dark:text-gray-400 block">
                      Email
                    </span>
                    <span className="text-gray-900 dark:text-white font-medium">
                      {bid?.contactEmail || "Not provided"}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500 dark:text-gray-400 block">
                      Phone
                    </span>
                    <span className="text-gray-900 dark:text-white font-medium">
                      {bid?.contactPhone || "Not provided"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  Venue Information
                </h3>
                <div className="space-y-2">
                  <div>
                    <span className="text-sm text-gray-500 dark:text-gray-400 block">
                      Venue Name
                    </span>
                    <span className="text-gray-900 dark:text-white font-medium">
                      {bid?.venueName || "Not provided"}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500 dark:text-gray-400 block">
                      Venue Address
                    </span>
                    <span className="text-gray-900 dark:text-white font-medium">
                      {bid?.venueAddress || "Not provided"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  Event Details
                </h3>
                <div className="space-y-2">
                  <div>
                    <span className="text-sm text-gray-500 dark:text-gray-400 block">
                      Proposed Dates
                    </span>
                    <span className="text-gray-900 dark:text-white font-medium">
                      {bid?.proposedDates || "Not provided"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  Equipment Information
                </h3>
                <div className="space-y-2">
                  <div>
                    <span className="text-sm text-gray-500 dark:text-gray-400 block">
                      Equipment Type
                    </span>
                    <span className="text-gray-900 dark:text-white font-medium">
                      {bid?.equipmentType ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100">
                          {bid.equipmentType}
                        </span>
                      ) : (
                        "Not provided"
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500 dark:text-gray-400 block">
                      Additional Equipment Details
                    </span>
                    <p className="text-gray-900 dark:text-white whitespace-pre-line">
                      {bid?.equipmentDetails || "Not provided"}
                    </p>
                  </div>
                </div>
              </div>

              {bid?.additionalInfo && (
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    Additional Information
                  </h3>
                  <p className="text-gray-900 dark:text-white whitespace-pre-line">
                    {bid.additionalInfo}
                  </p>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ChampionshipBidDetails;
